import { section, p, list, steps, tip, warn, table, faq } from './blocks';

export default {
  slug: 'website-bewertungsmanagement',
  title: 'Website- & Bewertungsmanagement',
  shortTitle: 'Website & Bewertungen',
  icon: 'Globe',
  updated: 'Juni 2026',
  datePublished: '2026-06-24',
  dateModified: '2026-06-24',
  image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1200&h=900&fit=crop',
  teaser: 'Der digitale Erstkontakt entscheidet: Was eine Restaurant-Website heute leisten muss, wie du dein Google-Unternehmensprofil optimierst und Bewertungen aktiv managst.',
  mainArticle: {
    title: 'Website und Bewertungsmanagement: Der Leitfaden für deine Online-Präsenz',
    takeaways: [
      'Gäste finden dich über Google-Profil und Bewertungen – die Website ist Vertiefung und Reservierungsziel.',
      'Drei Fragen in drei Sekunden beantworten: Was gibt es, wann ist offen, wie reserviere ich?',
      'Bewertungen aktiv sammeln, alle beantworten, wiederkehrende Kritik als Prozessproblem behandeln.',
      'Öffnungszeiten, Karte und Fotos müssen auf allen Kanälen identisch und aktuell sein.',
    ],
    intro: 'Bevor ein neuer Gast deine Tür öffnet, hat er dich schon besucht – auf Google, auf deiner Website, in den Bewertungen. Dieser digitale Erstkontakt entscheidet, ob er reserviert oder weiterscrollt. Dieser Leitfaden zeigt, wie du Website, Google-Profil und Bewertungen zu einem überzeugenden Gesamtbild machst.',
    sections: [
      section(
        'Wie Gäste dich heute finden',
        p('Der typische Weg eines neuen Gastes: Suche nach „Restaurant [Stadtteil]“ oder „Italiener in der Nähe“ auf dem Smartphone. Es erscheint die Kartenansicht mit drei bis fünf Betrieben, Sternebewertung und Fotos. Ein Klick auf das Profil: Öffnungszeiten, Bewertungen, Speisekarte, Reservierungsbutton. Erst danach – wenn überhaupt – folgt der Besuch der Website.'),
        p('Daraus folgt eine klare Prioritätenliste: **Google-Unternehmensprofil** zuerst, **Bewertungen** als Vertrauensbasis, **Website** als Vertiefung und Reservierungsziel. Alle drei müssen konsistent sein: gleiche Öffnungszeiten, gleiche Karte, gleiche Bilder.')
      ),
      section(
        'Die Restaurant-Website: Was wirklich drauf muss',
        p('Eine Restaurant-Website ist kein Magazin. Gäste kommen mit drei Fragen: Was gibt es, wann ist offen, wie reserviere ich? Alles andere ist Beiwerk.'),
        list([
          '**Startseite** mit Konzept in einem Satz, Öffnungszeiten, Reservierungsbutton und Adresse – ohne Scrollen sichtbar',
          '**Speisekarte** als HTML-Seite (nicht nur PDF), aktuell, mit Preisen und Allergenhinweisen',
          '**Reservierung** direkt eingebunden – kein Kontaktformular, kein „Rufen Sie an“',
          '**Kontakt & Anfahrt** mit Karte, Parkhinweisen, ÖPNV',
          '**Über uns** mit Gesichtern und Geschichte – kurz, aber persönlich',
          '**Gutscheine** online kaufbar',
          '**Jobs** – die Website ist dein wichtigstes Recruiting-Instrument',
          '**Impressum und Datenschutz** vollständig',
        ]),
        p('Weglassen kannst du: Bildergalerien ohne Kontext, News-Bereiche, die nie gepflegt werden, Intro-Animationen und alles, was den Weg zur Reservierung verlängert.')
      ),
      section(
        'Technische Basis',
        list([
          '**Mobil zuerst:** Über 70 % der Besuche kommen vom Smartphone – die Website muss dort perfekt funktionieren',
          '**Ladezeit:** Unter drei Sekunden; große Bilder komprimieren',
          '**SSL-Verschlüsselung** (https) – Standard und Vertrauenssignal',
          '**Barrierefreiheit:** Kontraste, Schriftgrößen, Alternativtexte – zunehmend auch gesetzlich relevant',
          '**Cookie-Einwilligung** nur, wo tatsächlich nötig; Tracking datenschutzkonform',
          '**Strukturierte Daten** für Restaurant, Öffnungszeiten und Speisekarte, damit Suchmaschinen sie direkt anzeigen',
          '**Pflegbarkeit:** Öffnungszeiten und Karte müssen ohne Agentur änderbar sein',
        ])
      ),
      section(
        'Lokale Sichtbarkeit: Das Google-Unternehmensprofil',
        p('Das Unternehmensprofil ist für die meisten Gäste die eigentliche „Website“. Vollständigkeit, Aktualität und Aktivität entscheiden über die Platzierung in der Kartenansicht. Pflege Kategorien, Öffnungszeiten (inklusive Feiertage), Attribute, Speisekarte, Fotos, Reservierungs- und Bestell-Links und veröffentliche regelmäßig Beiträge. Wie du das Profil Schritt für Schritt optimierst, zeigt der Vertiefungsartikel.'),
        tip('Prüfe deine Öffnungszeiten vor jedem Feiertag im Profil. „Geschlossen“ trotz geöffnet – oder umgekehrt – ist eine der häufigsten Ursachen für negative Bewertungen.')
      ),
      section(
        'Bewertungen: Warum sie so wichtig sind',
        p('Bewertungen sind digitale Mundpropaganda mit Reichweite. Sie beeinflussen die Platzierung in der lokalen Suche, die Klickrate auf dein Profil und die Entscheidung des Gastes. Dabei zählen vier Faktoren:'),
        list([
          '**Durchschnitt:** Ab 4,3 Sternen wird kaum noch gefiltert; unter 4,0 verlierst du viele Gäste',
          '**Anzahl:** Mehr Bewertungen wirken glaubwürdiger und stabilisieren den Durchschnitt',
          '**Aktualität:** Bewertungen aus dem letzten Monat zählen mehr als solche aus dem Vorjahr',
          '**Antwortquote:** Beantwortete Bewertungen signalisieren, dass sich jemand kümmert',
        ])
      ),
      section(
        'Bewertungen aktiv managen',
        p('Bewertungsmanagement ist ein Prozess mit vier Schritten, der wöchentlich 20 – 30 Minuten braucht:'),
        steps([
          '**Sammeln:** Zufriedene Gäste aktiv um Bewertung bitten – per QR-Code auf dem Bon, Karte am Tisch, Nachricht nach der Reservierung. Die meisten zufriedenen Gäste bewerten nur, wenn sie gefragt werden.',
          '**Beobachten:** Alle Plattformen im Blick behalten (Google, Bewertungsportale, Lieferplattformen, Social Media) – Benachrichtigungen einrichten.',
          '**Antworten:** Auf jede Bewertung – positive kurz und persönlich, kritische sachlich mit Lösung. Innerhalb von 48 Stunden.',
          '**Lernen:** Wiederkehrende Kritik ist ein Prozessproblem, kein Einzelfall. Monatlich auswerten und ins Team tragen.',
        ])
      ),
      section(
        'Umgang mit negativen und unfairen Bewertungen',
        p('Negative Bewertungen sind unvermeidlich – entscheidend ist der Umgang. Eine souveräne Antwort überzeugt oft mehr Leser als zehn Fünf-Sterne-Bewertungen. Bedanke dich für das Feedback, bedaure die Erfahrung, benenne konkret, was du daraus ableitest, und biete das persönliche Gespräch an. Keine Rechtfertigungen, keine Gegenangriffe.'),
        p('Bei Bewertungen, die offensichtlich falsch sind (kein Besuch, Verwechslung, Beleidigung, Wettbewerber), kannst du eine Löschung bei der Plattform beantragen. Halte Nachweise bereit – etwa Reservierungsdaten, die zeigen, dass die Person nicht da war. Bei rufschädigenden Falschbehauptungen kann rechtlicher Beistand sinnvoll sein.'),
        warn('Gekaufte Bewertungen oder Bewertungen von Mitarbeitern und Freunden verstoßen gegen die Plattformregeln und das Wettbewerbsrecht. Das Risiko einer Sperrung oder Abmahnung ist real – und echte Gäste erkennen gekaufte Bewertungen erstaunlich zuverlässig.')
      ),
      section(
        'Kosten und Betreuung',
        table(
          ['Baustein', 'Einmalig', 'Laufend'],
          [
            ['Website (Baukasten oder Agentur)', 'ca. 500 – 5.000 €', 'ca. 10 – 100 € / Monat Hosting und Wartung'],
            ['Professionelle Fotos', 'ca. 400 – 1.500 € pro Termin', 'Jährlich auffrischen'],
            ['Bewertungsmanagement-Tool', 'gering', 'ca. 20 – 100 € / Monat (optional)'],
            ['Google-Unternehmensprofil', '0 €', '30 Min. pro Woche Pflege'],
          ]
        ),
        p('Der wichtigste Kostenfaktor ist Zeit. Rechne mit einer festen Stunde pro Woche für Profil, Bewertungen und Aktualisierungen – und lege fest, wer sie hat.')
      ),
      section(
        'Fehler, die Gäste kosten',
        list([
          '**Speisekarte nur als PDF** oder gar nicht online',
          '**Unterschiedliche Öffnungszeiten** auf Website, Google und Social Media',
          '**Reservierung über Kontaktformular** – Anfragen werden erst Stunden später beantwortet',
          '**Website nicht mobil optimiert** oder mit Ladezeiten über fünf Sekunden',
          '**Bewertungen unbeantwortet** – vor allem die kritischen',
          '**Alte Fotos** von Gerichten, die nicht mehr auf der Karte stehen',
          '**Kein Prozess für Feiertagsöffnungszeiten**',
        ])
      ),
      section(
        'Häufige Fragen zur Online-Präsenz',
        faq([
          { q: 'Brauche ich überhaupt eine Website, wenn ich ein Google-Profil und Instagram habe?', a: 'Ja. Das Google-Profil gehört Google, Instagram gehört einem Konzern – deine Website gehört dir. Sie ist der einzige Ort, an dem du Reservierung, Karte, Gutscheine und Jobs vollständig kontrollierst, und sie ist das Ziel aller anderen Kanäle.' },
          { q: 'Darf ich Gäste um Bewertungen bitten?', a: 'Ja, solange du nicht selektiv nur zufriedene Gäste ansprichst, keine Gegenleistung anbietest und die Bewertung nicht vorformulierst. Eine neutrale Bitte („Wir freuen uns über dein Feedback“) mit QR-Code ist zulässig und wirksam.' },
          { q: 'Wie schnell sollte ich auf Bewertungen antworten?', a: 'Innerhalb von 48 Stunden, bei kritischen Bewertungen möglichst am selben Tag. Schnelle Antworten zeigen Lesern, dass Kritik ernst genommen wird.' },
          { q: 'Lohnt sich ein Tool für Bewertungsmanagement?', a: 'Ab mehreren Standorten oder vielen Plattformen ja – es bündelt alle Bewertungen an einem Ort und erinnert an offene Antworten. Für einen Standort reichen meist die Benachrichtigungen der Plattformen und ein fester Wochentermin.' },
        ])
      ),
    ],
  },
  subArticles: [
    {
      slug: 'google-unternehmensprofil',
      title: 'Google-Unternehmensprofil optimieren: Die Checkliste für Gastronomen',
      shortTitle: 'Google-Unternehmensprofil',
      teaser: 'Das Profil ist deine wichtigste digitale Visitenkarte. Welche Felder du pflegen musst, wie du in der Kartenansicht nach oben kommst und welche Fehler Gäste kosten.',
      intro: 'Für viele Gäste ist das Google-Unternehmensprofil der einzige Kontakt vor dem Besuch – und trotzdem sind die meisten Profile unvollständig, veraltet oder unbeaufsichtigt. Eine Stunde Pflege macht hier oft mehr Unterschied als jede Anzeige.',
      sections: [
        section(
          'Warum das Profil so wichtig ist',
          p('Bei lokalen Suchen zeigt die Suchmaschine zuerst die Kartenansicht mit wenigen Betrieben. Wer dort erscheint, bekommt den Großteil der Klicks. Die Platzierung hängt von Relevanz (passen Kategorie und Angebot?), Entfernung und Bekanntheit (Bewertungen, Aktivität, Vollständigkeit) ab. Zwei der drei Faktoren kannst du direkt beeinflussen.')
        ),
        section(
          'Vollständigkeits-Checkliste',
          steps([
            '**Name:** Exakt wie am Eingang – keine Zusätze wie „bestes Sushi Hamburg“',
            '**Hauptkategorie und Nebenkategorien:** So spezifisch wie möglich (z. B. „Italienisches Restaurant“ statt „Restaurant“), Nebenkategorien für Bar, Café, Lieferservice',
            '**Öffnungszeiten:** Inklusive abweichender Zeiten an Feiertagen und Betriebsferien – im Voraus eintragen',
            '**Adresse, Telefon, Website:** Konsistent mit allen anderen Kanälen',
            '**Reservierungs- und Bestell-Link:** Direkt zu deinem System, nicht zu einer Plattform mit Provision',
            '**Speisekarte:** Als Link zur HTML-Karte oder direkt im Profil hinterlegt',
            '**Attribute:** Außenbereich, barrierefrei, vegetarische Optionen, Zahlungsarten, WLAN, Hunde erlaubt – alles, was zutrifft',
            '**Beschreibung:** 750 Zeichen zu Konzept, Küche, Atmosphäre – ohne Werbefloskeln',
            '**Fotos:** Außenansicht, Innenraum, Gerichte, Team – mindestens 20, regelmäßig ergänzt',
          ])
        ),
        section(
          'Aktivität zeigen: Beiträge, Fragen, Fotos',
          p('Aktive Profile werden bevorzugt. Nutze die Funktion für Beiträge wöchentlich – Wochenkarte, Event, Saisonstart, Aktion. Beantworte Fragen im Bereich „Fragen und Antworten“ und stelle dort selbst häufige Fragen samt Antwort ein (Parken, Reservierung, Kinderstühle, Hunde). Lade jeden Monat neue Fotos hoch – Gästefotos ergänzen, ersetzen aber nicht deine eigenen.'),
          tip('Fotos von Gästen landen automatisch im Profil – auch die schlechten. Sorge dafür, dass deine eigenen, guten Bilder in der Mehrheit sind, damit sie prominenter erscheinen.')
        ),
        section(
          'Bewertungen im Profil',
          p('Bewertungen sind der sichtbarste Teil des Profils. Antworte auf jede, bitte zufriedene Gäste aktiv um Feedback (QR-Code mit Direktlink zur Bewertungsseite) und melde offensichtlich falsche Bewertungen über die Profilverwaltung. Wie du auf Bewertungen antwortest, zeigt der eigene Vertiefungsartikel.')
        ),
        section(
          'Kennzahlen im Profil nutzen',
          p('Die Profilstatistik zeigt, wie oft dein Betrieb gefunden wurde, über welche Suchbegriffe, wie viele Anrufe, Routenanfragen, Website-Klicks und Reservierungen daraus entstanden sind. Prüfe monatlich: Steigen Routenanfragen und Reservierungsklicks? Welche Suchbegriffe bringen Gäste? Fehlt eine Kategorie, nach der gesucht wird?')
        ),
        section(
          'Häufige Fehler',
          list([
            'Doppelte Profile (z. B. nach Umzug oder Inhaberwechsel) – zusammenführen lassen',
            'Feiertagsöffnungszeiten nicht gepflegt – „geschlossen“ trotz Öffnung',
            'Reservierungslink führt zu einer Plattform mit Gebühren statt zum eigenen System',
            'Keine Antworten auf Bewertungen',
            'Beschreibung und Fotos seit Jahren unverändert',
            'Zugang zum Profil liegt bei einer ehemaligen Agentur oder einem ausgeschiedenen Mitarbeiter',
          ])
        ),
      ],
    },
    {
      slug: 'auf-bewertungen-antworten',
      title: 'Auf Bewertungen richtig antworten – mit Textbausteinen für jeden Fall',
      shortTitle: 'Auf Bewertungen antworten',
      teaser: 'Positiv, kritisch, unfair oder gefälscht: Wie du souverän auf jede Bewertung reagierst, welche Formulierungen funktionieren und was du unbedingt vermeiden solltest.',
      intro: 'Deine Antwort auf eine Bewertung lesen nicht nur der Verfasser, sondern hunderte potenzielle Gäste. Sie ist damit Teil deines Marketings. Gute Antworten sind kurz, persönlich und lösungsorientiert – und folgen einem einfachen Muster.',
      sections: [
        section(
          'Grundregeln für jede Antwort',
          list([
            'Innerhalb von 48 Stunden antworten – bei Kritik am selben Tag',
            'Persönlich unterschreiben (Vorname, Rolle), nicht als „Das Team“',
            'Den Gast direkt ansprechen, konkret auf den Inhalt eingehen – keine Standardfloskeln',
            'Kurz halten: drei bis fünf Sätze reichen',
            'Niemals öffentlich streiten, niemals Details über den Gast preisgeben',
            'Bei Kritik: Ins Gespräch außerhalb der Plattform einladen (Telefon, E-Mail)',
          ])
        ),
        section(
          'Textbaustein: Positive Bewertung',
          p('„Vielen Dank, Lisa, für die schönen Worte! Dass dir gerade das Risotto so gut geschmeckt hat, freut unsere Küche besonders – das Rezept stammt von unserem Souschef Marco. Wir freuen uns auf deinen nächsten Besuch. Herzliche Grüße, Anna (Inhaberin)“'),
          p('Wirkungsvoll ist, ein Detail aus der Bewertung aufzugreifen. Das zeigt, dass du gelesen hast, und gibt anderen Lesern ein weiteres Argument für den Besuch.')
        ),
        section(
          'Textbaustein: Kritische, aber faire Bewertung',
          p('„Lieber Herr Weber, danke für Ihr ehrliches Feedback. Dass Sie am Samstag so lange auf das Hauptgericht warten mussten, tut uns leid – das entspricht nicht unserem Anspruch. Wir haben den Abend im Team besprochen und die Abläufe zwischen Küche und Service für volle Abende angepasst. Ich würde mich freuen, wenn Sie uns eine zweite Chance geben – melden Sie sich gern direkt bei mir unter [E-Mail]. Herzliche Grüße, Anna (Inhaberin)“'),
          tip('Die Formel: Danken – Bedauern – Konkrete Konsequenz – Einladung zum Gespräch. Keine Rechtfertigung („Wir waren unterbesetzt“), keine Relativierung („Normalerweise klappt das“).')
        ),
        section(
          'Textbaustein: Unfaire oder überzogene Bewertung',
          p('„Guten Tag, danke für Ihre Rückmeldung. Wir bedauern, dass Ihr Besuch nicht Ihren Erwartungen entsprochen hat. Einige der geschilderten Punkte können wir aus unserer Sicht nicht nachvollziehen, nehmen Ihr Feedback aber ernst. Für ein persönliches Gespräch stehe ich gern zur Verfügung unter [Telefon]. Freundliche Grüße, Anna (Inhaberin)“'),
          p('Ruhig, höflich, ohne Zugeständnisse, die nicht zutreffen. Leser erkennen an der Tonalität, wer sich souverän verhält – die Antwort ist für sie geschrieben, nicht für den Verfasser.')
        ),
        section(
          'Textbaustein: Offensichtlich falsche oder gefälschte Bewertung',
          p('„Guten Tag, wir haben Ihre Bewertung geprüft und können weder eine Reservierung noch einen Besuch unter diesem Namen zuordnen. Sollte es sich um eine Verwechslung handeln, melden Sie sich bitte bei uns. Wir haben die Bewertung zur Prüfung an die Plattform weitergeleitet. Freundliche Grüße, Anna (Inhaberin)“'),
          p('Parallel die Bewertung über die Plattform melden und Nachweise bereithalten. Ein sachlicher öffentlicher Hinweis schützt deinen Ruf bei anderen Lesern, auch wenn die Löschung Wochen dauert.')
        ),
        section(
          'Was du vermeiden solltest',
          list([
            'Kopierte Standardantworten unter jeder Bewertung',
            'Öffentliche Diskussionen über Details des Besuchs oder der Rechnung',
            'Gegenangriffe („Sie haben sich selbst unmöglich verhalten“)',
            'Gutscheine oder Rabatte öffentlich anbieten – das lädt zu Missbrauch ein',
            'Bewertungen ignorieren, weil „nur die Kritiker schreiben“',
            'Mitarbeiter öffentlich beschuldigen oder Konsequenzen ankündigen',
          ])
        ),
      ],
    },
    {
      slug: 'restaurant-website-checkliste',
      title: 'Restaurant-Website: Die Checkliste für Struktur, Inhalte und Pflege',
      shortTitle: 'Website-Checkliste',
      teaser: 'Welche Seiten deine Website braucht, welche Inhalte auf jede gehören und wie du sicherstellst, dass sie aktuell bleibt – ohne Agentur für jede Änderung.',
      intro: 'Eine gute Restaurant-Website ist unspektakulär: Sie beantwortet drei Fragen in drei Sekunden und führt zur Reservierung. Diese Checkliste hilft beim Neubau ebenso wie beim Aufräumen einer bestehenden Seite.',
      sections: [
        section(
          'Die Seitenstruktur',
          table(
            ['Seite', 'Pflichtinhalte', 'Hinweis'],
            [
              ['Startseite', 'Konzept in einem Satz, Öffnungszeiten, Reservierungsbutton, Adresse, 3 – 5 starke Bilder', 'Alles Wichtige ohne Scrollen auf dem Smartphone'],
              ['Speisekarte', 'Aktuelle Karte als HTML mit Preisen, Allergenen, ggf. Tages-/Wochenkarte', 'Kein PDF-Download als einzige Option'],
              ['Reservieren', 'Eingebettetes Reservierungs-Widget, Hinweis für Gruppen', 'Direkt buchbar, keine Anfrage per Formular'],
              ['Kontakt & Anfahrt', 'Adresse, Karte, Telefon, E-Mail, Parken, ÖPNV', 'Klickbare Telefonnummer'],
              ['Über uns', 'Team, Geschichte, Philosophie mit Fotos', 'Persönlich und kurz'],
              ['Gutscheine', 'Online-Kauf mit sofortigem Versand', 'Besonders im 4. Quartal wertvoll'],
              ['Jobs', 'Offene Stellen, Arbeitsalltag, Bewerbung in 2 Minuten', 'Wichtigstes Recruiting-Instrument'],
              ['Impressum / Datenschutz', 'Vollständig nach geltendem Recht', 'Von jeder Seite erreichbar'],
            ]
          )
        ),
        section(
          'Inhalte, die Gäste überzeugen',
          list([
            'Echte Fotos aus deinem Betrieb – keine Stockbilder',
            'Konkrete Sprache: „Hausgemachte Pasta, täglich frisch“ statt „kulinarische Erlebnisse“',
            'Antworten auf typische Fragen: Kinderstühle, Hunde, Barrierefreiheit, vegane Optionen, Gruppen',
            'Aktuelle Hinweise: Betriebsferien, Feiertage, Events',
            'Bewertungsauszüge oder Auszeichnungen – sparsam und echt',
          ])
        ),
        section(
          'Lokale Suchmaschinenoptimierung – die Basics',
          p('Du musst kein SEO-Experte sein. Ein paar Grundlagen sorgen dafür, dass Suchmaschinen deine Website richtig einordnen:'),
          list([
            'Seitentitel mit Konzept und Ort („Trattoria Rossi – Italienisches Restaurant in Hamburg-Ottensen“)',
            'Adresse und Öffnungszeiten als Text auf der Seite, nicht nur als Bild',
            'Strukturierte Daten für Restaurant, Öffnungszeiten, Speisekarte, Bewertungen',
            'Verlinkung von und zu deinem Google-Unternehmensprofil und Social-Media-Profilen',
            'Schnelle Ladezeit, mobile Optimierung, aussagekräftige Bild-Alternativtexte',
          ])
        ),
        section(
          'Pflege sicherstellen',
          p('Die meisten Restaurant-Websites veralten innerhalb eines Jahres, weil jede Änderung eine Agentur erfordert. Achte darauf, dass Öffnungszeiten, Speisekarte und Hinweise **selbst** in wenigen Minuten änderbar sind – über ein Baukastensystem, ein einfaches Redaktionssystem oder eine direkte Anbindung an dein Kassensystem für die Karte.'),
          tip('Lege einen festen Monatstermin fest: Karte prüfen, Öffnungszeiten prüfen, Feiertage der nächsten acht Wochen eintragen, ein neues Foto hochladen. 15 Minuten, die deine Website dauerhaft aktuell halten.')
        ),
        section(
          'Häufige Fehler',
          list([
            'Speisekarte veraltet oder mit falschen Preisen',
            'Reservierung nur per Telefon oder Kontaktformular',
            'Startseite mit Intro-Video oder Animation, die den Weg zur Information blockiert',
            'Mobil unleserlich: winzige Schrift, nicht klickbare Telefonnummer',
            'Kein Zugriff auf die eigene Domain oder das Hosting – liegt bei einer alten Agentur',
            'Kontaktformular ohne Empfänger – Anfragen laufen ins Leere',
          ])
        ),
      ],
    },
  ],
};
